import { Box, Button, Card, CardContent, Chip, Container, Fab, Paper, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import './ColorPaletteShowcase.css';
import LawAppBar from './LawAppBar';
import Footer from './Footer';
import GavelIcon from '@mui/icons-material/Gavel';
import LoginIcon from '@mui/icons-material/Login';

// Define color palette options
const colorPalettes = {
  classic: {
    name: 'Classic Gold & Brass',
    description: 'Traditional scales of justice colors with rich gold and deep brass tones',
    colors: {
      primary: '#D4AF37',
      primaryDark: '#B8860B',
      secondary: '#8B7355',
      accent: '#F4D03F',
      background: '#FFFFFF',
      surface: '#FDF8E8',
      surfaceHover: '#F9F3D5',
      border: '#E8DCC0',
      borderHover: '#D4C4A0',
      textPrimary: '#1A1A1A',
      textSecondary: '#4A4A4A',
      textMuted: '#8B8B8B',
      gradientStart: '#D4AF37',
      gradientEnd: '#B8860B',
      gradientHoverStart: '#E5C158',
      gradientHoverEnd: '#C8961C',
    }
  },
  rich: {
    name: 'Rich Bronze & Gold',
    description: 'Sophisticated bronze and gold combination for a premium feel',
    colors: {
      primary: '#CD7F32',
      primaryDark: '#8B4513',
      secondary: '#A0522D',
      accent: '#FFD700',
      background: '#FFFFFF',
      surface: '#FFF8E7',
      surfaceHover: '#FFF0D0',
      border: '#E8D4B8',
      borderHover: '#D4B896',
      textPrimary: '#1A1A1A',
      textSecondary: '#4A4A4A',
      textMuted: '#8B8B8B',
      gradientStart: '#CD7F32',
      gradientEnd: '#FFD700',
      gradientHoverStart: '#D8904A',
      gradientHoverEnd: '#FFE033',
    }
  },
  warm: {
    name: 'Warm Amber & Brass',
    description: 'Lighter, more approachable amber tones with classic brass accents',
    colors: {
      primary: '#FFBF00',
      primaryDark: '#B87333',
      secondary: '#D4A574',
      accent: '#FFD700',
      background: '#FFFFFF',
      surface: '#FFF9E6',
      surfaceHover: '#FFF3CC',
      border: '#F0E0B8',
      borderHover: '#E0D0A0',
      textPrimary: '#1A1A1A',
      textSecondary: '#4A4A4A',
      textMuted: '#8B8B8B',
      gradientStart: '#FFBF00',
      gradientEnd: '#B87333',
      gradientHoverStart: '#FFCC33',
      gradientHoverEnd: '#C8834A',
    }
  },
  elegant: {
    name: 'Elegant Gold & Charcoal',
    description: 'High contrast professional palette with bright gold and deep charcoal',
    colors: {
      primary: '#F4D03F',
      primaryDark: '#D4AF37',
      secondary: '#2C2C2C',
      accent: '#FFD700',
      background: '#FFFFFF',
      surface: '#F5F5F5',
      surfaceHover: '#EEEEEE',
      border: '#E0E0E0',
      borderHover: '#D0D0D0',
      textPrimary: '#1A1A1A',
      textSecondary: '#2C2C2C',
      textMuted: '#666666',
      gradientStart: '#F4D03F',
      gradientEnd: '#2C2C2C',
      gradientHoverStart: '#F5D85F',
      gradientHoverEnd: '#3C3C3C',
    }
  },
  antique: {
    name: 'Antique Brass & Cream',
    description: 'Vintage-inspired palette with antique brass and warm cream tones',
    colors: {
      primary: '#C9A961',
      primaryDark: '#8B7355',
      secondary: '#D4B896',
      accent: '#E6D5B8',
      background: '#FEFCF8',
      surface: '#F9F6F0',
      surfaceHover: '#F3EFE5',
      border: '#E8DCC0',
      borderHover: '#D4C4A0',
      textPrimary: '#2C2416',
      textSecondary: '#4A3E2E',
      textMuted: '#8B7D6B',
      gradientStart: '#C9A961',
      gradientEnd: '#8B7355',
      gradientHoverStart: '#D9B971',
      gradientHoverEnd: '#9B8365',
    }
  }
};

export default function ColorPaletteShowcase() {
  const [selectedPalette, setSelectedPalette] = useState('classic');

  const currentPalette = colorPalettes[selectedPalette];

  const applyPalette = (paletteKey) => {
    const palette = colorPalettes[paletteKey];
    const root = document.documentElement;
    
    // Apply CSS variables
    root.style.setProperty('--primary-color', palette.colors.primary);
    root.style.setProperty('--secondary-color', palette.colors.secondary);
    root.style.setProperty('--accent-color', palette.colors.accent);
    root.style.setProperty('--background-color', palette.colors.background);
    root.style.setProperty('--surface-color', palette.colors.surface);
    root.style.setProperty('--surface-hover', palette.colors.surfaceHover);
    root.style.setProperty('--border-color', palette.colors.border);
    root.style.setProperty('--border-hover', palette.colors.borderHover);
    root.style.setProperty('--text-primary', palette.colors.textPrimary);
    root.style.setProperty('--text-secondary', palette.colors.textSecondary);
    root.style.setProperty('--text-muted', palette.colors.textMuted);
    root.style.setProperty('--gradient-primary-start', palette.colors.gradientStart);
    root.style.setProperty('--gradient-primary-end', palette.colors.gradientEnd);
    root.style.setProperty('--gradient-primary-hover-start', palette.colors.gradientHoverStart);
    root.style.setProperty('--gradient-primary-hover-end', palette.colors.gradientHoverEnd);
  };

  const handlePaletteSelect = (paletteKey) => {
    setSelectedPalette(paletteKey);
    applyPalette(paletteKey);
  };

  // Apply palette when selected changes
  useEffect(() => {
    applyPalette(selectedPalette);
  }, [selectedPalette]);

  return (
    <div className="App">
      <LawAppBar cookies={{}} />
      <Container sx={{ maxWidth: '1400px', padding: '20px' }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'var(--text-primary)',
              fontWeight: 400,
              fontSize: '2.5rem',
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}
          >
            AttorneyAide.com Color Palette Options
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'var(--text-secondary)',
              fontSize: '1.1rem',
              maxWidth: 800,
              margin: '0 auto'
            }}
          >
            Explore color palettes inspired by the scales of justice. Each palette draws from gold and brass tones to create a professional, trustworthy aesthetic for legal professionals.
          </Typography>
        </Box>

        {/* Palette Selector */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {Object.keys(colorPalettes).map((key) => (
            <Button
              key={key}
              variant={selectedPalette === key ? 'contained' : 'outlined'}
              onClick={() => handlePaletteSelect(key)}
              sx={{
                minWidth: 180,
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                ...(selectedPalette === key && {
                  background: `linear-gradient(135deg, ${colorPalettes[key].colors.gradientStart} 0%, ${colorPalettes[key].colors.gradientEnd} 100%)`,
                  color: '#FFFFFF',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colorPalettes[key].colors.gradientHoverStart} 0%, ${colorPalettes[key].colors.gradientHoverEnd} 100%)`,
                  }
                })
              }}
            >
              {colorPalettes[key].name}
            </Button>
          ))}
        </Box>

        {/* Logo/Heading Showcase */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 6,
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--background-color)',
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h2"
            component="div"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              mb: 2,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              flexWrap: 'wrap'
            }}
          >
            {/* First A - Left Scale */}
            <Box
              component="span"
              sx={{
                position: 'relative',
                color: currentPalette.colors.primary,
                fontSize: '3.5rem',
                fontWeight: 700,
                display: 'inline-block',
                background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60%',
                  height: '3px',
                  background: `linear-gradient(90deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                  borderRadius: '2px',
                  boxShadow: `0 2px 8px ${currentPalette.colors.primary}40`
                }
              }}
            >
              A
            </Box>
            
            {/* Middle text */}
            <Box
              component="span"
              sx={{
                color: 'var(--text-primary)',
                fontSize: '3.5rem',
                fontWeight: 300,
                letterSpacing: '0.05em'
              }}
            >
              ttorney
            </Box>
            
            {/* Second A - Right Scale */}
            <Box
              component="span"
              sx={{
                position: 'relative',
                color: currentPalette.colors.primary,
                fontSize: '3.5rem',
                fontWeight: 700,
                display: 'inline-block',
                background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60%',
                  height: '3px',
                  background: `linear-gradient(90deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                  borderRadius: '2px',
                  boxShadow: `0 2px 8px ${currentPalette.colors.primary}40`
                }
              }}
            >
              A
            </Box>
            
            {/* Rest of text */}
            <Box
              component="span"
              sx={{
                color: 'var(--text-primary)',
                fontSize: '3.5rem',
                fontWeight: 300,
                letterSpacing: '0.05em'
              }}
            >
              ide.com
            </Box>
          </Typography>
          
          {/* Scales connector line */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              height: '2px',
              background: `linear-gradient(90deg, transparent 0%, ${currentPalette.colors.border} 20%, ${currentPalette.colors.accent} 50%, ${currentPalette.colors.border} 80%, transparent 100%)`,
              mb: 3,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: currentPalette.colors.accent,
                boxShadow: `0 0 12px ${currentPalette.colors.accent}60`
              }
            }}
          />
          
          <Typography
            variant="body1"
            sx={{
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              fontStyle: 'italic',
              mb: 3
            }}
          >
            The two A's represent the balanced scales of justice
          </Typography>
          
          {/* App Bar Logo Preview */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid var(--border-color)'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-muted)',
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block'
              }}
            >
              App Bar Logo Preview
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '8px 16px',
                borderRadius: '8px',
                background: 'var(--surface-color)',
                border: '1px solid var(--border-color)'
              }}
            >
              {/* First A - Left Scale */}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  display: 'inline-block',
                  background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50%',
                    height: '2px',
                    background: `linear-gradient(90deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                    borderRadius: '1px'
                  }
                }}
              >
                A
              </Box>
              
              {/* Middle text */}
              <Box
                component="span"
                sx={{
                  color: 'var(--text-primary)',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  letterSpacing: '0.02em'
                }}
              >
                ttorney
              </Box>
              
              {/* Second A - Right Scale */}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  display: 'inline-block',
                  background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50%',
                    height: '2px',
                    background: `linear-gradient(90deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                    borderRadius: '1px'
                  }
                }}
              >
                A
              </Box>
              
              {/* Rest of text */}
              <Box
                component="span"
                sx={{
                  color: 'var(--text-primary)',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  letterSpacing: '0.02em'
                }}
              >
                ide.com
              </Box>
            </Box>
          </Box>
          
          {/* Favicon Exploration */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid var(--border-color)'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-muted)',
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 3,
                display: 'block'
              }}
            >
              Favicon Options - "AA" Styles
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {/* Option 1: Simple AA with scales underline */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--surface-color)',
                    border: '2px solid var(--border-color)',
                    borderRadius: '12px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '70%',
                          height: '3px',
                          background: `linear-gradient(90deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                          borderRadius: '2px'
                        }
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '70%',
                          height: '3px',
                          background: `linear-gradient(90deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                          borderRadius: '2px'
                        }
                      }}
                    >
                      A
                    </Box>
                  </Box>
                  {/* Connector line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: '2px',
                      background: `linear-gradient(90deg, transparent 0%, ${currentPalette.colors.accent} 50%, transparent 100%)`,
                      borderRadius: '1px'
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  With Scales
                </Typography>
              </Box>

              {/* Option 2: Simple AA without scales */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--surface-color)',
                    border: '2px solid var(--border-color)',
                    borderRadius: '12px'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      A
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  Simple
                </Typography>
              </Box>

              {/* Option 3: AA with gold solid colors */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${currentPalette.colors.surface} 0%, ${currentPalette.colors.background} 100%)`,
                    border: `2px solid ${currentPalette.colors.primary}`,
                    borderRadius: '12px'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: currentPalette.colors.primary
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: currentPalette.colors.accent
                      }}
                    >
                      A
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  Gold Solid
                </Typography>
              </Box>

              {/* Option 4: AA with dark background and gold text */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)`,
                    borderRadius: '12px',
                    boxShadow: `0 4px 12px rgba(0,0,0,0.3)`
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        letterSpacing: '-0.05em'
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        letterSpacing: '-0.05em'
                      }}
                    >
                      A
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  Dark BG + Gold
                </Typography>
              </Box>

              {/* Option 5: AA with circular dark background and gold text */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `radial-gradient(circle, #1a1a1a 0%, #2c2c2c 100%)`,
                    borderRadius: '50%',
                    boxShadow: `0 4px 12px rgba(0,0,0,0.3)`
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        letterSpacing: '-0.05em'
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        letterSpacing: '-0.05em'
                      }}
                    >
                      A
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  Circular + Gold
                </Typography>
              </Box>

              {/* Option 6: AA with dark square background, gold text and scales */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)`,
                    borderRadius: '8px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: `0 4px 12px rgba(0,0,0,0.3)`
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '70%',
                          height: '2px',
                          background: `linear-gradient(90deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                          borderRadius: '1px',
                          opacity: 0.9
                        }
                      }}
                    >
                      A
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '70%',
                          height: '2px',
                          background: `linear-gradient(90deg, ${currentPalette.colors.gradientEnd} 0%, ${currentPalette.colors.gradientStart} 100%)`,
                          borderRadius: '1px',
                          opacity: 0.9
                        }
                      }}
                    >
                      A
                    </Box>
                  </Box>
                  {/* Connector line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '50%',
                      height: '2px',
                      background: `linear-gradient(90deg, transparent 0%, ${currentPalette.colors.accent} 50%, transparent 100%)`,
                      borderRadius: '1px',
                      opacity: 0.7
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  Dark BG + Gold Scales
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Current Palette Display */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ mb: 2, color: 'var(--text-primary)', fontWeight: 500 }}>
            {currentPalette.name}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'var(--text-secondary)' }}>
            {currentPalette.description}
          </Typography>

          {/* Color Swatches */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--surface-color)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: 'var(--text-primary)' }}>
              Color Swatches
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(currentPalette.colors).map(([name, color]) => (
                <Box key={name} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '8px',
                      background: color,
                      border: '1px solid var(--border-color)',
                      mb: 1,
                      boxShadow: 'var(--shadow-light)'
                    }}
                  />
                  <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                    {color}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Component Examples */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'var(--text-primary)' }}>
              Component Examples
            </Typography>

            {/* Buttons */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--background-color)'
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'var(--text-primary)', fontWeight: 500 }}>
                Buttons
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Fab
                  variant="extended"
                  size="large"
                  sx={{
                    background: `linear-gradient(135deg, ${currentPalette.colors.gradientStart} 0%, ${currentPalette.colors.gradientEnd} 100%)`,
                    boxShadow: `0 6px 20px ${currentPalette.colors.gradientStart}40`,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.05rem',
                    padding: '15px 28px',
                    borderRadius: '16px',
                    color: '#FFFFFF',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 30px ${currentPalette.colors.gradientStart}60`,
                      background: `linear-gradient(135deg, ${currentPalette.colors.gradientHoverStart} 0%, ${currentPalette.colors.gradientHoverEnd} 100%)`
                    }
                  }}
                >
                  <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Primary Button
                </Fab>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LoginIcon sx={{ fontSize: '1.25rem' }} />}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.05rem',
                    padding: '15px 28px',
                    borderRadius: '16px',
                    minHeight: '56px',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                    borderWidth: '2px',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'var(--accent-color)',
                      borderWidth: '2px',
                      backgroundColor: 'var(--surface-color)',
                      boxShadow: `0 4px 12px ${currentPalette.colors.accent}20`
                    }
                  }}
                >
                  Secondary Button
                </Button>
              </Box>
            </Paper>

            {/* Cards */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--background-color)'
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'var(--text-primary)', fontWeight: 500 }}>
                Cards
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Card
                  elevation={0}
                  sx={{
                    flex: '1 1 300px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)',
                    background: 'var(--background-color)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--shadow-medium)',
                      borderColor: 'var(--accent-color)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, color: 'var(--text-primary)', fontWeight: 500 }}>
                      Case Document
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
                      Medical report from Dr. Smith dated January 15, 2024
                    </Typography>
                    <Chip
                      label="Processed"
                      size="small"
                      sx={{
                        background: 'var(--success-color)',
                        color: '#FFFFFF',
                        fontWeight: 500
                      }}
                    />
                  </CardContent>
                </Card>
                <Card
                  elevation={0}
                  sx={{
                    flex: '1 1 300px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)',
                    background: 'var(--surface-color)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 'var(--shadow-medium)',
                      borderColor: 'var(--accent-color)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, color: 'var(--text-primary)', fontWeight: 500 }}>
                      Medical Chronology
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
                      Timeline of medical events and treatments
                    </Typography>
                    <Chip
                      label="In Progress"
                      size="small"
                      sx={{
                        background: 'var(--warning-color)',
                        color: '#FFFFFF',
                        fontWeight: 500
                      }}
                    />
                  </CardContent>
                </Card>
              </Box>
            </Paper>

            {/* Text Examples */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--background-color)'
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'var(--text-primary)', fontWeight: 500 }}>
                Typography
              </Typography>
              <Typography variant="h4" sx={{ mb: 1, color: 'var(--text-primary)' }}>
                Heading Text
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
                Body text with secondary color for descriptions and supporting content.
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                Muted text for less important information or metadata.
              </Typography>
            </Paper>

            {/* Status Colors */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--background-color)'
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'var(--text-primary)', fontWeight: 500 }}>
                Status Indicators
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label="Success" sx={{ background: 'var(--success-color)', color: '#FFFFFF', fontWeight: 500 }} />
                <Chip label="Warning" sx={{ background: 'var(--warning-color)', color: '#FFFFFF', fontWeight: 500 }} />
                <Chip label="Error" sx={{ background: 'var(--error-color)', color: '#FFFFFF', fontWeight: 500 }} />
                <Chip label="Info" sx={{ background: 'var(--info-color)', color: '#FFFFFF', fontWeight: 500 }} />
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* CSS Variable Mapping */}
        <Box sx={{ mt: 6, mb: 6 }}>
          <Typography variant="h4" sx={{ mb: 3, color: 'var(--text-primary)', fontWeight: 500 }}>
            CSS Variable Mapping
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'var(--text-secondary)' }}>
            This table shows which CSS variables from App.css are used in the codebase and how they map to the selected color palette. Status colors remain consistent across all palettes for accessibility.
          </Typography>
          
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--background-color)',
              overflow: 'auto'
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2
              }}
            >
              {/* Text Colors */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Text Colors
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--text-primary', palette: 'textPrimary', label: 'Text Primary' },
                    { var: '--text-secondary', palette: 'textSecondary', label: 'Text Secondary' },
                    { var: '--text-muted', palette: 'textMuted', label: 'Text Muted' },
                    { var: '--text-disabled', palette: 'textMuted', label: 'Text Disabled' },
                  ].map(({ var: varName, palette: paletteKey, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: currentPalette.colors[paletteKey],
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {currentPalette.colors[paletteKey]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Background Colors */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Background Colors
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--background-color', palette: 'background', label: 'Background' },
                    { var: '--surface-color', palette: 'surface', label: 'Surface' },
                    { var: '--surface-hover', palette: 'surfaceHover', label: 'Surface Hover' },
                    { var: '--hover-bg', palette: 'surfaceHover', label: 'Hover Background' },
                    { var: '--table-header-bg', palette: 'surface', label: 'Table Header' },
                  ].map(({ var: varName, palette: paletteKey, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: currentPalette.colors[paletteKey],
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {currentPalette.colors[paletteKey]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Border Colors */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Border Colors
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--border-color', palette: 'border', label: 'Border' },
                    { var: '--border-hover', palette: 'borderHover', label: 'Border Hover' },
                    { var: '--timeline-connector', palette: 'border', label: 'Timeline Connector' },
                  ].map(({ var: varName, palette: paletteKey, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: currentPalette.colors[paletteKey],
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {currentPalette.colors[paletteKey]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Accent & Primary Colors */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Accent & Primary
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--accent-color', palette: 'accent', label: 'Accent' },
                    { var: '--primary-color', palette: 'primary', label: 'Primary' },
                    { var: '--secondary-color', palette: 'secondary', label: 'Secondary' },
                    { var: '--timeline-dot', palette: 'accent', label: 'Timeline Dot' },
                    { var: '--selected-border', palette: 'accent', label: 'Selected Border' },
                  ].map(({ var: varName, palette: paletteKey, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: currentPalette.colors[paletteKey],
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {currentPalette.colors[paletteKey]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Gradient Colors */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Gradient Colors
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--gradient-primary-start', palette: 'gradientStart', label: 'Gradient Start' },
                    { var: '--gradient-primary-end', palette: 'gradientEnd', label: 'Gradient End' },
                    { var: '--gradient-primary-hover-start', palette: 'gradientHoverStart', label: 'Gradient Hover Start' },
                    { var: '--gradient-primary-hover-end', palette: 'gradientHoverEnd', label: 'Gradient Hover End' },
                  ].map(({ var: varName, palette: paletteKey, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: `linear-gradient(135deg, ${currentPalette.colors[paletteKey]} 0%, ${currentPalette.colors[paletteKey]} 100%)`,
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {currentPalette.colors[paletteKey]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Status Colors (Fixed) */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Status Colors (Fixed)
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'var(--text-muted)', fontSize: '0.7rem', fontStyle: 'italic' }}>
                  These colors remain consistent across all palettes for accessibility
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--success-color', color: '#10b981', label: 'Success' },
                    { var: '--warning-color', color: '#f59e0b', label: 'Warning' },
                    { var: '--error-color', color: '#ef4444', label: 'Error' },
                    { var: '--info-color', color: '#3b82f6', label: 'Info' },
                  ].map(({ var: varName, color, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: color,
                          border: '1px solid var(--border-color)',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {color}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Shadow & Other */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem' }}>
                  Shadows & Other
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { var: '--shadow-subtle', value: '0 1px 3px rgba(0,0,0,0.1)', label: 'Shadow Subtle' },
                    { var: '--shadow-light', value: '0 2px 8px rgba(0,0,0,0.08)', label: 'Shadow Light' },
                    { var: '--shadow-medium', value: '0 4px 15px rgba(0,0,0,0.12)', label: 'Shadow Medium' },
                    { var: '--shadow-heavy', value: '0 8px 25px rgba(0,0,0,0.15)', label: 'Shadow Heavy' },
                    { var: '--button-gradient', value: 'linear-gradient(...)', label: 'Button Gradient' },
                    { var: '--button-gradient-hover', value: 'linear-gradient(...)', label: 'Button Hover' },
                    { var: '--selected-bg', value: 'rgba(66, 127, 250, 0.1)', label: 'Selected BG' },
                  ].map(({ var: varName, value, label }) => (
                    <Box key={varName} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '4px', background: 'var(--surface-color)' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          background: 'var(--surface-color)',
                          border: '2px dashed var(--border-color)',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.5rem' }}>
                          ...
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.75rem' }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                          {varName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          {value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* All Palettes Overview */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ mb: 3, color: 'var(--text-primary)', fontWeight: 500, textAlign: 'center' }}>
            All Palette Options
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {Object.entries(colorPalettes).map(([key, palette]) => (
              <Card
                key={key}
                elevation={0}
                onClick={() => handlePaletteSelect(key)}
                sx={{
                  borderRadius: '8px',
                  border: selectedPalette === key ? '2px solid var(--accent-color)' : '1px solid var(--border-color)',
                  background: 'var(--background-color)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 'var(--shadow-medium)',
                    borderColor: 'var(--accent-color)'
                  }
                }}
              >
                <Box
                  sx={{
                    height: 120,
                    background: `linear-gradient(135deg, ${palette.colors.gradientStart} 0%, ${palette.colors.gradientEnd} 100%)`,
                    borderRadius: '8px 8px 0 0'
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, color: 'var(--text-primary)', fontWeight: 500 }}>
                    {palette.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {palette.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '4px', background: palette.colors.primary, border: '1px solid var(--border-color)' }} />
                    <Box sx={{ width: 40, height: 40, borderRadius: '4px', background: palette.colors.accent, border: '1px solid var(--border-color)' }} />
                    <Box sx={{ width: 40, height: 40, borderRadius: '4px', background: palette.colors.secondary, border: '1px solid var(--border-color)' }} />
                    <Box sx={{ width: 40, height: 40, borderRadius: '4px', background: palette.colors.surface, border: '1px solid var(--border-color)' }} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
      <Footer />
    </div>
  );
}

